import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import { Link, Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionGiftStockEvidence } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

/**
 * The gift stock card, as the companion panel shows it.
 *
 * One card per brand, about the offered product to look at first; the others
 * running short are counted, never listed. Two gestures and never a third:
 * restock in the Shopify admin (the card's call to action) and replace the
 * product in each flow offering it. Pausing admissions does not resolve stock
 * shortages or stop existing waits, so this card keeps its stock-related actions.
 */

interface GiftStockCardProps {
  action: CompanionActionDto & { evidence: CompanionGiftStockEvidence };
  /** Opens the product in the Shopify admin. Wired by the panel, never here. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

export const GiftStockCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: GiftStockCardProps) => {
  const { t } = useTranslation();
  const { evidence } = action;
  const out = evidence.severity === 'out';

  return (
    <CompanionCardShell
      testId="companion-gift-stock-card"
      dataAttrs={{ 'data-shopify-product-id': evidence.shopifyProductId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader
        title={
          out ? t('companion.cards.giftStock.titleOut') : t('companion.cards.giftStock.titleLow')
        }
        reason={action.reason}
      />

      <Stack gap="2px">
        <EvidenceLine>
          {out
            ? t('companion.cards.giftStock.noUnitsLeft')
            : t('companion.cards.giftStock.unitsLeft', { count: evidence.unitsLeft })}
        </EvidenceLine>
        {evidence.giftsWaiting > 0 ? (
          <EvidenceLine>
            {t('companion.cards.giftStock.giftsWaiting', { count: evidence.giftsWaiting })}
          </EvidenceLine>
        ) : null}
        {evidence.heldAffiliates > 0 ? (
          <EvidenceLine>
            {t('companion.cards.giftStock.heldAffiliates', { count: evidence.heldAffiliates })}
          </EvidenceLine>
        ) : null}
        {evidence.otherShortProducts > 0 ? (
          <EvidenceLine>
            {t('companion.cards.giftStock.otherProducts', { count: evidence.otherShortProducts })}
          </EvidenceLine>
        ) : null}
      </Stack>

      {evidence.flows.length > 0 ? (
        <Stack gap="2px">
          {evidence.flows.map((flow) => (
            <Text key={flow.id} fontSize="12.5px" lineHeight="1.5">
              <Link asChild color="ink" fontWeight="600" textDecoration="underline">
                <RouterLink to={`/flows/${flow.id}`}>
                  {t('companion.cards.giftStock.replaceInFlow', { name: flow.name })}
                </RouterLink>
              </Link>
            </Text>
          ))}
        </Stack>
      ) : null}
    </CompanionCardShell>
  );
};
